import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import {
	CreativeUnitWithCustomizationsDto
} from '../../../../../../../api/src/creative-unit-package/models/creative-unit/creative-unit.dto';

@Component({
	selector: 'app-ad-preview-zoom',
	templateUrl: './ad-preview-zoom.component.html',
	styleUrls: ['./ad-preview-zoom.component.scss']
})
export class AdPreviewZoomComponent {
	readonly MAX_HEIGHT_CHANGE_STEP = 50;
	readonly MAX_HEIGHT_MIN_VALUE = 100;
	readonly MAX_HEIGHT_MAX_VALUE = 700;
	readonly MAX_HEIGHT_SMALL_VALUE = 250;

	readonly PREVIEW_SCALE_MIN_VALUE = 0.25;
	readonly PREVIEW_SCALE_MAX_VALUE = 10;
	readonly PREVIEW_SCALE_STEP = 0.1;
	readonly PREVIEW_FILL_PADDING = 5; // padding when calculating the zoom to fill to ensure a better user experience

	// Keyboard shortcuts
	readonly KEYBOARD_ZOOM_IN = ['+', '='];    // +/= key
	readonly KEYBOARD_ZOOM_OUT = ['-', '_', '/'];   // -/_ key
	readonly KEYBOARD_ZOOM_RESET = ['0'];        // 0 key
	readonly KEYBOARD_ZOOM_FILL = ['f', 'F'];         // f key

	@Input() activeItem: CreativeUnitWithCustomizationsDto | null = null;
	@Input() previewScale: string = 'fit';
	@Input() previewMaxHeight: number = 700;


	@Output() previewScaleChange: EventEmitter<number> = new EventEmitter();
	@Output() previewMaxHeightChange: EventEmitter<number | undefined> = new EventEmitter();

	@HostListener('document:keydown', ['$event']) onDocumentKeyDown(event: KeyboardEvent) { this.documentKeyDown(event); }
	private lastKeyTimeStamp = 0; // for prevent multiple keydown events when key is held down


	getPreviewScaleValue(): string {
		if (this.previewScale === 'fit') {
			return (this.PREVIEW_SCALE_MAX_VALUE + this.PREVIEW_SCALE_MIN_VALUE) / 2 + '';
		}

		return this.previewScale;
	}

	parseFloatValue(value: string): number {
		return parseFloat(value);
	}


	documentKeyDown(event: KeyboardEvent) {
		const hasZoomModifier = event.ctrlKey || event.metaKey;
		const isModifierOnly = hasZoomModifier && (event.key === 'Control' || event.key === 'Meta');

		if (!hasZoomModifier || isModifierOnly || this.isInputFieldFocused(event) || !this.activeItem) {
			return;
		}

		if (this.lastKeyTimeStamp && (event.timeStamp - this.lastKeyTimeStamp) < 100) {
			// Prevent handling multiple keydown events when the key is held down
			event.preventDefault();
			return;
		}
		this.lastKeyTimeStamp = event.timeStamp;

		if (this.KEYBOARD_ZOOM_IN.includes(event.key)) {
			event.preventDefault();
			this.handleZoomIn();
		} else if (this.KEYBOARD_ZOOM_OUT.includes(event.key)) {
			event.preventDefault();
			this.handleZoomOut();
		} else if (this.KEYBOARD_ZOOM_RESET.includes(event.key)) {
			event.preventDefault();
			this.handleZoomReset();
		} else if (this.KEYBOARD_ZOOM_FILL.includes(event.key)) {
			event.preventDefault();
			this.handleZoomFill();
		}
	}

	handleZoomIn(): void {
		const currentZoom = this.parseFloatValue(this.getPreviewScaleValue());
		const newZoom = Math.min(currentZoom + this.PREVIEW_SCALE_STEP, this.PREVIEW_SCALE_MAX_VALUE);
		this.previewScaleChange.emit(newZoom);
	}

	handleZoomOut(): void {
		const currentZoom = this.parseFloatValue(this.getPreviewScaleValue());
		const newZoom = Math.max(currentZoom - this.PREVIEW_SCALE_STEP, this.PREVIEW_SCALE_MIN_VALUE);
		this.previewScaleChange.emit(newZoom);
	}

	handleZoomReset(): void {
		this.previewScaleChange.emit(1);
	}

	handleZoomFill(): void {
		const zoomLevel = this.calculateZoomToFill();
		this.previewScaleChange.emit(zoomLevel);
	}

	private calculateZoomToFill(): number {
		const rect = this.getPreviewContainerDimensions();
		if (!rect) {
			return 1;
		}
		const { width, height } = rect;

		if (!width || !height || !this.activeItem || !this.activeItem.dimensions?.width || !this.activeItem.dimensions.height) {
			return 1;
		}
		const adWidth = this.activeItem.dimensions.width + this.PREVIEW_FILL_PADDING;
		const adHeight = this.activeItem.dimensions.height + this.PREVIEW_FILL_PADDING;

		const scaleByWidth = width / adWidth;
		const scaleByHeight = height / adHeight;

		const calculatedScale = Math.min(scaleByWidth, scaleByHeight);

		return Math.max(
			this.PREVIEW_SCALE_MIN_VALUE,
			Math.min(calculatedScale, this.PREVIEW_SCALE_MAX_VALUE)
		);
	}

	private getPreviewContainerDimensions(): { width: number; height: number } | undefined {
		const previewContainer = document.querySelector('.ad-preview-container');
		if (previewContainer && previewContainer instanceof HTMLElement) {
			return previewContainer.getBoundingClientRect();
		}
	}

	private isInputFieldFocused(event: KeyboardEvent): boolean {
		const target = event.target as HTMLElement;
		return ['INPUT', 'TEXTAREA'].includes(target?.tagName);
	}
}
